<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  productName: {
    type: String,
    default: 'Product',
  },
  useCompanyInfo: {
    type: Boolean,
    default: true,
  },
  selectedCountry: {
    type: String,
    default: '',
  },
  selectedLanguage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:useCompanyInfo',
  'update:selectedCountry',
  'update:selectedLanguage',
  'refresh',
]);

const activeTab = ref('information');

const tabs = [
  { key: 'information', label: 'Information', icon: 'i-lucide-info' },
  { key: 'media', label: 'Media', icon: 'i-lucide-image' },
  { key: 'testimonial', label: 'Testimonial', icon: 'i-lucide-message-circle' },
  { key: 'certificate', label: 'Certificate', icon: 'i-lucide-award' },
  { key: 'price', label: 'Price', icon: 'i-lucide-dollar-sign' },
  {
    key: 'answerTemplate',
    label: 'Answer Template',
    icon: 'i-lucide-file-text',
  },
];

const countryOptions = [
  { value: '', label: 'Select a Country' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'CN', label: 'China' },
];

const languageOptions = [
  { value: '', label: 'Select a Language' },
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'ms', label: 'Bahasa Malaysia' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'th', label: 'ไทย' },
];

const localUseCompanyInfo = computed({
  get: () => props.useCompanyInfo,
  set: value => emit('update:useCompanyInfo', value),
});

const localCountry = computed({
  get: () => props.selectedCountry,
  set: value => emit('update:selectedCountry', value),
});

const localLanguage = computed({
  get: () => props.selectedLanguage,
  set: value => emit('update:selectedLanguage', value),
});

const handleRefresh = () => {
  emit('refresh');
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Product Name Header -->
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-bold text-n-slate-12">{{ productName }}</h1>
    </div>

    <!-- Tab Navigation -->
    <div class="border-b border-n-slate-3">
      <nav class="flex gap-0 -mb-px overflow-x-auto">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
          :class="
            activeTab === tab.key
              ? 'border-woot-500 text-woot-500'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-4'
          "
          @click="activeTab = tab.key"
        >
          <span :class="tab.icon" class="w-4 h-4" />
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <!-- Company Info Toggle -->
    <div class="flex items-center gap-3">
      <label class="relative inline-flex items-center cursor-pointer">
        <input
          v-model="localUseCompanyInfo"
          type="checkbox"
          class="sr-only peer"
        />
        <div
          class="w-11 h-6 bg-n-slate-4 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-woot-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
        />
      </label>
      <span class="text-sm text-n-slate-11">
        Use information provided by company, will auto update periodically.
      </span>
    </div>

    <!-- Filters Row -->
    <div class="flex items-center gap-3">
      <!-- Country Select -->
      <select
        v-model="localCountry"
        class="px-4 py-2.5 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 min-w-[160px]"
      >
        <option
          v-for="option in countryOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Language Select -->
      <select
        v-model="localLanguage"
        class="px-4 py-2.5 text-sm bg-white dark:bg-n-slate-2 border border-n-slate-4 rounded-xl text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 min-w-[180px]"
      >
        <option
          v-for="option in languageOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Refresh Button -->
      <button
        class="flex items-center justify-center w-10 h-10 bg-woot-500 hover:bg-woot-600 text-white rounded-xl transition-colors"
        @click="handleRefresh"
      >
        <span class="i-lucide-refresh-cw w-4 h-4" />
      </button>

      <!-- Spacer -->
      <div class="flex-1" />

      <!-- View Toggle -->
      <div class="flex gap-1 p-1 bg-n-slate-2 rounded-lg">
        <button
          class="p-2 rounded-md hover:bg-white dark:hover:bg-n-slate-1 transition-colors"
        >
          <span class="i-lucide-list w-4 h-4 text-n-slate-11" />
        </button>
        <button class="p-2 rounded-md bg-white dark:bg-n-slate-1 shadow-sm">
          <span class="i-lucide-grid-2x2 w-4 h-4 text-n-slate-12" />
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="min-h-[400px]">
      <slot :name="activeTab" :active-tab="activeTab">
        <!-- Default slot content if no named slot provided -->
        <div class="flex items-center justify-center h-64 text-n-slate-10">
          <div class="text-center">
            <span class="i-lucide-inbox w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No content for {{ activeTab }} tab</p>
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>
